/**
 * Squadre (ADR-0033 Phase F) — router-based multi-agent delegation, the 7th AI-plane
 * entity. A team is a ROUTER agent that delegates a turn to one of its MEMBER agents
 * (each a `delegate_to_<key>` tool). The runtime is boundary-parametrized: this console
 * runs it on the operator Ask boundary; binding a team to a portal interface (Interfacce)
 * runs it under the role's RLS — a member never reads more than the router (the keystone).
 *
 * "Squadra" deliberately ≠ "orchestration" (already the workflow plane's n8n kind).
 * Under the `ai` macro-area. Native controls are intentional: this is the operator console.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Play, Plus, Trash2, Users } from "lucide-react";

import { usePermissions } from "@/lib/permissions";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  type AiAgent,
  type AiTeam,
  type TeamRunEvent,
  agentsApi,
  streamTeamRun,
  teamsApi,
} from "@/lib/api-ai";

const inputCls = "rounded-md border border-border bg-background px-2 py-1.5 text-sm";
const btnCls =
  "inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-sm hover:bg-muted disabled:opacity-50";

const KEY = ["ai", "teams"] as const;
const AGENTS_KEY = ["ai", "agents"] as const;

export function TeamsPage() {
  const { can } = usePermissions();
  const canWrite = can("teams", "write");
  const canDelete = can("teams", "delete");
  const qc = useQueryClient();
  const q = useQuery({ queryKey: KEY, queryFn: () => teamsApi.list() });
  const agentsQ = useQuery({ queryKey: AGENTS_KEY, queryFn: () => agentsApi.list() });

  const remove = useMutation({
    mutationFn: (id: string) => teamsApi.remove(id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: KEY }),
  });

  const [editing, setEditing] = useState<AiTeam | null>(null);
  const [creating, setCreating] = useState(false);

  const teams = q.data?.teams ?? [];
  const agents = agentsQ.data?.agents ?? [];
  const agentName = (id: string | null) => agents.find((a) => a.id === id)?.name ?? "—";

  return (
    <div className="mx-auto max-w-4xl p-6">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-lg font-semibold">
            <Users className="h-4 w-4 text-muted-foreground" /> Squadre
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Una squadra è un agente <strong>router</strong> che delega il compito a uno
            dei suoi agenti <strong>membri</strong>. Il membro gira sempre nello stesso
            confine-dati del router (non vede mai più di lui). Collega una squadra a
            un'interfaccia del portale per usarla con i ruoli, oppure provala qui.
          </p>
        </div>
        {canWrite && (
          <button type="button" className={btnCls} onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Nuova
          </button>
        )}
      </div>

      {q.isLoading && <p className="text-sm text-muted-foreground">Caricamento…</p>}
      {teams.length === 0 && !q.isLoading ? (
        <p className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nessuna squadra. Creane una scegliendo un agente router e i membri a cui può
          delegare (es. un router che smista tra un agente "vendite" e uno "supporto").
        </p>
      ) : (
        teams.length > 0 && (
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2">Squadra</th>
                <th className="py-2">Router</th>
                <th className="py-2">Membri</th>
                <th className="py-2">Stato</th>
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {teams.map((tm) => (
                <tr key={tm.id} className="border-b border-border/60 align-top">
                  <td className="py-2 font-medium">
                    {tm.name}
                    <span className="ml-2 rounded bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                      {tm.key}
                    </span>
                    {tm.description && (
                      <p className="mt-0.5 text-xs font-normal text-muted-foreground">
                        {tm.description}
                      </p>
                    )}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground">
                    {agentName(tm.router_agent_id)}
                  </td>
                  <td className="py-2 text-xs text-muted-foreground">
                    {tm.member_agent_ids.length}
                  </td>
                  <td className="py-2">
                    <span className={tm.enabled ? "text-success text-xs" : "text-muted-foreground text-xs"}>
                      {tm.enabled ? "attiva" : "disattivata"}
                    </span>
                  </td>
                  <td className="py-2">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        className="rounded p-1 hover:bg-muted"
                        title={canWrite ? "Modifica" : "Apri"}
                        onClick={() => setEditing(tm)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      {canDelete && (
                        <button
                          type="button"
                          className="rounded p-1 text-danger hover:bg-muted"
                          title="Elimina"
                          disabled={remove.isPending}
                          onClick={() => {
                            if (window.confirm(`Eliminare la squadra “${tm.name}”?`)) remove.mutate(tm.id);
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      {(creating || editing) && (
        <TeamDialog
          team={editing}
          agents={agents}
          canWrite={canWrite}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => void qc.invalidateQueries({ queryKey: KEY })}
        />
      )}
    </div>
  );
}

function TeamDialog({
  team,
  agents,
  canWrite,
  onClose,
  onSaved,
}: {
  team: AiTeam | null;
  agents: AiAgent[];
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [savedId, setSavedId] = useState<string | null>(team?.id ?? null);
  const [key, setKey] = useState(team?.key ?? "");
  const [name, setName] = useState(team?.name ?? "");
  const [description, setDescription] = useState(team?.description ?? "");
  const [routerId, setRouterId] = useState<string>(team?.router_agent_id ?? "");
  const [memberIds, setMemberIds] = useState<string[]>(team?.member_agent_ids ?? []);
  const [routing, setRouting] = useState(team?.routing_instructions ?? "");
  const [enabled, setEnabled] = useState(team?.enabled ?? true);
  const [error, setError] = useState<string | null>(null);

  // A team composes CUSTOM agents (ADR-0071), so a system agent is offered NEITHER as
  // router NOR as member. Odino is the operator's rail-only builder: no capabilities to
  // grant, its own strategy + privileged tools — as a router it degrades to a nameless
  // brain with no tools, and a team can be bound to an app's chat/voice, which would drag
  // the system agent's name onto a portal surface. The backend REFUSES it
  // (`team:router_is_system` / `member_is_system`); this filter is the courtesy that keeps
  // the operator from meeting that error at all.
  // NB the filter is HERE, not on the page's `agents` — that list also resolves a team's
  // router NAME for display, and a pre-existing system-routed team must still read as
  // "Odino", not "—".
  const selectable = agents.filter((a) => !a.is_system);
  // Members are the other agents (a router delegating to itself is nonsensical).
  const memberCandidates = selectable.filter((a) => a.id !== routerId);

  const save = useMutation({
    mutationFn: () => {
      const body = {
        name,
        description: description || null,
        router_agent_id: routerId,
        member_agent_ids: memberIds,
        routing_instructions: routing || null,
        enabled,
      };
      return savedId ? teamsApi.update(savedId, body) : teamsApi.create({ key, ...body });
    },
    onSuccess: (saved) => {
      setSavedId(saved.id);
      onSaved();
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : "Errore"),
  });

  function toggleMember(id: string) {
    setMemberIds((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold">
            {savedId ? "Modifica squadra" : "Nuova squadra"}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-muted-foreground">Nome</span>
              <input className={`${inputCls} mt-1 w-full`} value={name}
                     onChange={(e) => setName(e.target.value)} placeholder="Supporto clienti" />
            </label>
            <label className="block">
              <span className="text-xs text-muted-foreground">Chiave (immutabile)</span>
              <input className={`${inputCls} mt-1 w-full font-mono`} value={key}
                     disabled={!!savedId}
                     onChange={(e) => setKey(e.target.value)} placeholder="supporto" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs text-muted-foreground">Descrizione</span>
            <input className={`${inputCls} mt-1 w-full`} value={description}
                   onChange={(e) => setDescription(e.target.value)} />
          </label>
          <label className="block">
            <span className="text-xs text-muted-foreground">Agente router (decide e compone)</span>
            <select className={`${inputCls} mt-1 w-full`} value={routerId}
                    onChange={(e) => {
                      setRouterId(e.target.value);
                      setMemberIds((m) => m.filter((x) => x !== e.target.value));
                    }}>
              <option value="">— scegli —</option>
              {selectable.map((a) => (
                <option key={a.id} value={a.id}>{a.name}</option>
              ))}
            </select>
          </label>
          <div>
            <span className="text-xs text-muted-foreground">Membri (a cui il router può delegare)</span>
            <div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-md border border-border p-2">
              {memberCandidates.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nessun altro agente disponibile.</p>
              ) : (
                memberCandidates.map((a) => (
                  <label key={a.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={memberIds.includes(a.id)}
                           onChange={() => toggleMember(a.id)} />
                    {a.name}
                    {!a.enabled && (
                      <span className="text-[10px] text-muted-foreground">(disattivato)</span>
                    )}
                  </label>
                ))
              )}
            </div>
          </div>
          <label className="block">
            <span className="text-xs text-muted-foreground">Istruzioni di smistamento (opzionale)</span>
            <textarea className={`${inputCls} mt-1 w-full`} rows={3} value={routing}
                      onChange={(e) => setRouting(e.target.value)}
                      placeholder="Es. Delega le domande sui pagamenti a 'fatturazione', il resto a 'supporto'." />
          </label>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Attiva
          </label>
          </div>

          {error && <p className="mt-3 text-sm text-danger">{error}</p>}

          {savedId && <TeamConsole teamId={savedId} />}
        </DialogBody>

        <DialogFooter>
          <button type="button" className={btnCls} onClick={onClose}>Chiudi</button>
          {canWrite && (
            <button
              type="button"
              className={`${btnCls} bg-primary text-primary-foreground hover:bg-primary/90`}
              disabled={save.isPending || !name.trim() || (!savedId && !key.trim()) || !routerId}
              onClick={() => { setError(null); save.mutate(); }}
            >
              {savedId ? "Salva" : "Crea"}
            </button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TeamConsole({ teamId }: { teamId: string }) {
  const [input, setInput] = useState("");
  const [answer, setAnswer] = useState("");
  const [steps, setSteps] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function onEvent(ev: TeamRunEvent) {
    switch (ev.type) {
      case "delta":
        if (typeof ev.text === "string") setAnswer((a) => a + ev.text);
        break;
      case "final":
        if (typeof ev.text === "string") setAnswer(ev.text);
        break;
      case "tool": {
        const nm = String((ev as { name?: string }).name ?? "");
        if (nm.startsWith("delegate_to_")) setSteps((s) => [...s, `→ delega a ${nm.slice(12)}`]);
        else if (nm) setSteps((s) => [...s, `· ${nm}`]);
        break;
      }
      case "error":
        setErr(String(ev.message ?? "Errore"));
        break;
    }
  }

  async function run() {
    const q = input.trim();
    if (!q || running) return;
    setErr(null);
    setAnswer("");
    setSteps([]);
    setRunning(true);
    try {
      await streamTeamRun(teamId, q, onEvent);
    } catch {
      setErr("Esecuzione fallita.");
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="mt-5 border-t border-border pt-4">
      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-semibold">
        <Play className="h-3.5 w-3.5" /> Prova la squadra
      </h4>
      <p className="mb-2 text-xs text-muted-foreground">
        Esegue un turno sul confine-dati operatore (Ask): tutti i dataset pubblicati,
        nessuna maschera di ruolo. Il router può delegare ai membri.
      </p>
      <div className="flex gap-2">
        <input
          className={`${inputCls} flex-1`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") void run(); }}
          placeholder="Fai una domanda…"
        />
        <button type="button" className={btnCls} disabled={running || !input.trim()} onClick={() => void run()}>
          <Play className="h-3.5 w-3.5" /> {running ? "Esecuzione…" : "Prova"}
        </button>
      </div>

      {err && <p className="mt-2 text-sm text-danger">{err}</p>}
      {steps.length > 0 && (
        <ul className="mt-3 space-y-0.5 font-mono text-xs text-muted-foreground">
          {steps.map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
      {answer && (
        <div className="mt-3 whitespace-pre-wrap rounded-md border border-border bg-muted/30 p-3 text-sm">
          {answer}
        </div>
      )}
    </div>
  );
}
